import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { api, User, Role, setCsrfToken } from '../lib/api';

interface AuthState {
  user: User | null;
  role: Role | null;
  postCount: number;
  setUser: (u: User | null) => void;
  setRole: (r: Role | null) => void;
  refreshAuth: () => Promise<void>;
  loading: boolean;
}

const AuthContext = createContext<AuthState>({
  user: null,
  role: null,
  postCount: 0,
  setUser: () => {},
  setRole: () => {},
  refreshAuth: async () => {},
  loading: true,
});

const SSO_REAUTH_KEY = 'apoo_sso_reauth_at';
const SSO_REAUTH_COOLDOWN_MS = 5 * 60 * 1000;

/**
 * The forum session is gone but this browser signed in through SSO and never
 * logged out: bounce through the identity provider, which signs the user
 * straight back in (and returns them to this page) while they're still logged
 * in there. Returns true when the page is navigating away.
 */
function startSilentReauth(): boolean {
  if (/\/(login|register)(\/|$)/.test(window.location.pathname)) return false;
  // Rate-limit per tab so a failing round trip can never become a redirect loop.
  // Without sessionStorage there's no guard, so don't attempt it at all.
  try {
    const last = Number(sessionStorage.getItem(SSO_REAUTH_KEY) || 0);
    if (Date.now() - last < SSO_REAUTH_COOLDOWN_MS) return false;
    sessionStorage.setItem(SSO_REAUTH_KEY, String(Date.now()));
  } catch {
    return false;
  }
  const { pathname, search, hash } = window.location;
  window.location.replace(api.auth.oidcLoginUrl({ returnTo: pathname + search + hash, silent: true }));
  return true;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [role, setRole] = useState<Role | null>(null);
  const [postCount, setPostCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const loadAuth = useCallback(async () => {
    const res = await api.auth.me();
    setUser(res.data.user);
    setRole(res.data.role || null);
    setPostCount(res.data.post_count || 0);
    setCsrfToken(res.data.csrf_token ?? null);
    return res.data;
  }, []);

  const refreshAuth = useCallback(async () => {
    await loadAuth();
  }, [loadAuth]);

  useEffect(() => {
    let redirecting = false;
    loadAuth()
      .then((data) => {
        if (!data.user && data.sso_reauth) redirecting = startSilentReauth();
      })
      .catch(() => {})
      // Stay in the loading state while navigating away, so the page doesn't
      // flash a logged-out view.
      .finally(() => { if (!redirecting) setLoading(false); });
  }, [loadAuth]);

  return (
    <AuthContext.Provider value={{ user, role, postCount, setUser, setRole, refreshAuth, loading }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
